import React, { useEffect, useMemo, useState } from "react";
import { Button, Icon, ImageCard, Text, Divider } from "@ilc-technology/luik";
import QuoteHeader from "./QuoteHeader/QuoteHeader";
import BannerImage from "../BannerImage/BannerImage";
import Enrich from "../../Common/services/TextEnricher";
import {
  CourseContent,
  ProgramCodes,
  Quote,
  PaymentInfoStatus,
  PaymentTypeEnum,
  QuotePaymentStatus,
  SalesItemType,
  SegmentType,
  UpgradeCourseCode,
} from "../../Common/Types";
import { useDatasources } from "../../contexts/StoryblokContext/StoryblokContext";
import { isQuotePreferred, useQuoteContext } from "../../contexts/QuoteContext/QuoteContext";
import { AppConfig } from "../../AppConfig";
import { LabelKey } from "../../Common/StoryblokTypes";
import QuoteSegment from "./QuoteSegment/QuoteSegment";
import {
  TRUE,
  UPSELL_ACTIVATION_DATE,
  MAX_DURATION_FOR_UPGRADE_WEEKS,
  SINGLE_WEEK_DURATION,
  MULTI_WEEK_DURATION_THRESHOLD,
  IMAGE_CARD_ASPECT_RATIO,
} from "../../Common/Constants";
import UpgradeCourse from "../UpgradeCourse/UpgradeCourse";
import { ContentType, CustomTrackingEvent, StandardTrackingEvent, trackEvent } from "../../Common/services/Analytics";
import UpgradeDuration from "../UpgradeDuration/UpgradeDuration";
import { UpgradeAccommodation } from "../UpgradeAccommodation/UpgradeAccommodation";
import {
  LineItemIsExtraNight,
  mapFactSheetDestinationCode,
} from "../../Common/Helpers/LineItemHelper";
import { UpgradeOptionalItems } from "../UpgradeOptionalItems/UpgradeOptionalItems";
import { getOptionalItems } from "../../Common/services/UpgradeService";
import { QuoteDraft } from "../../contexts/QuoteDetailsContext";
import { generatePriceQuoteEventItems } from "../../Common/services/AnalyticsMappings";
import { getDestinationName, getQuoteInformation, QuoteInformation } from "../../Common/services/QuoteService";
import { getCourseContentForQuote } from "../../Common/services/CourseContentService";
import { getQuoteMartechContentParams } from "../../Common/services/MartechContentParamsService";
import { getPaymentForPaidInFullDiscountRow, isQuoteFullyPaid } from "../../Common/services/PaymentsService";
import ContentLayout from "../BaseComponents/ContentLayout";
import TBSection from "../BaseComponents/TBSection";
import BookNow from "../BookNow/BookNow";

type CourseItems = {
  destinationCode: string;
  courses: Array<CourseContent>;
};

interface QuoteDetailsProps {
  quote: Quote;
  quoteDraft: QuoteDraft;
}

function calculateCurrentSegmentIndex(quoteData: Quote, quoteInfo: QuoteInformation) {
  return quoteData.segments.findIndex((segment) => segment.id === quoteInfo.courseSegments[0].id);
}

const QuoteDetails: React.FC<QuoteDetailsProps> = ({ quote, quoteDraft }) => {
  const quoteData = quoteDraft.quoteDraft.quoteData;
  const { labels, featureSettings, programs, activeLanguage } = useDatasources();
  const { quotesContentCache, isQuotesDestinationsLoading, quotes, preferredQuoteId, setPreferredQuoteId } =
    useQuoteContext();
  const quoteInfo = getQuoteInformation(quoteDraft.quoteDraft.quoteData);

  const firstCourseSegmentCode = quoteInfo.courseSegments[0]?.course.code;
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(calculateCurrentSegmentIndex(quoteData, quoteInfo));
  const [isGroupCourse, setGroupCourse] = useState(false);
  const [courseContent, setCourseContent] = useState<Array<CourseItems>>();
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const isCourseIntensityUpgradeEnabled = featureSettings.isCourseIntensityUpgradeEnabled === TRUE;
  const isDurationUpgradeEnabled = featureSettings.isDurationUpgradeEnabled === TRUE;
  const isAccommodationUpgradeEnabled = featureSettings.isAccommodationUpgradeEnabled === TRUE;
  const isUpgradeOptionalItemsEnabled = featureSettings.isUpgradeOptionalItemsEnabled === TRUE;
  const showRawQuote = new URLSearchParams(window.location.search).get("showRawQuote") === "true";
  const isPreferredQuoteEnabled = featureSettings.isPreferredQuoteEnabled === TRUE;
  const quoteFromList = quotes.find((listQuote) => listQuote.quoteData.id === quoteData.id);
  const canSetAsPreferred = !isQuoteFullyPaid(quote.payments);
  const isPreferred =
    isPreferredQuoteEnabled &&
    canSetAsPreferred &&
    isQuotePreferred(quoteFromList ?? quoteDraft.quoteDraft, preferredQuoteId);
  const currentSegment = quoteData.segments[currentSegmentIndex];
  const paymentForPaidInFullDiscount = getPaymentForPaidInFullDiscountRow(quote.payments);

  const isPaidInFullCheckout =
    paymentForPaidInFullDiscount?.paymentType === PaymentTypeEnum.PAID_IN_FULL ||
    paymentForPaidInFullDiscount?.paymentType === PaymentTypeEnum.PAID_IN_FULL_WITH_DISCOUNT;
  const showPayInFullDiscountRow =
    quote.status === QuotePaymentStatus.Paid &&
    paymentForPaidInFullDiscount?.status === PaymentInfoStatus.Paid &&
    isPaidInFullCheckout;

  const rawAppliedDiscount = paymentForPaidInFullDiscount?.appliedDiscount;
  const payNowDiscountAmount =
    showPayInFullDiscountRow && rawAppliedDiscount != null && !Number.isNaN(Number(rawAppliedDiscount))
      ? Math.abs(Number(rawAppliedDiscount))
      : undefined;

  const isInternationalLanguageProgram =
    quoteData.program === ProgramCodes.ILC || quoteData.program === ProgramCodes.ILS;
  const upsellActivationDate = new Date(UPSELL_ACTIVATION_DATE);
  const hasSomeOptionalItems = quoteData.segments.some(
    (_, segIdx) => getOptionalItems(segIdx, quote, quoteData).length > 0
  );
  const isUpgradeOptionalItemsAvailable = isUpgradeOptionalItemsEnabled && hasSomeOptionalItems;
  const isCourseUpgradeAvailable =
    !quoteInfo.isMultiQuote &&
    isCourseIntensityUpgradeEnabled &&
    !isGroupCourse &&
    isInternationalLanguageProgram &&
    (firstCourseSegmentCode === UpgradeCourseCode.Basic || firstCourseSegmentCode === UpgradeCourseCode.General) &&
    new Date(quoteData.createdAt) >= upsellActivationDate;
  const upgradeCourseCode =
    firstCourseSegmentCode === UpgradeCourseCode.Basic ? UpgradeCourseCode.General : UpgradeCourseCode.Intensive;
  const isAccommodationUpgradeAvailable =
    !quoteInfo.isMultiQuote &&
    isAccommodationUpgradeEnabled &&
    quoteInfo.courseSegments?.[0]?.lineItems.find(
      (lineItem) =>
        lineItem.type === SalesItemType.Accommodation && lineItem.isOptional && !LineItemIsExtraNight(lineItem)
    ) &&
    quoteInfo.courseSegments?.[0]?.lineItems.filter(
      (lineItem) =>
        lineItem.type === SalesItemType.Accommodation && !lineItem.isOptional && !LineItemIsExtraNight(lineItem)
    ).length === 1;
  const { market, efcomProduct, courseContentProduct } = getQuoteMartechContentParams({
    quote,
    quoteData,
    programs,
    defaultProgramCode: AppConfig.efcom.defaultProgramCode,
    isGroupCourse,
  });
  const additionalCourseCodes = isCourseUpgradeAvailable ? [upgradeCourseCode] : [];

  const weeksLabel =
    quoteInfo.totalDuration === SINGLE_WEEK_DURATION
      ? labels[LabelKey.durationWeek]
      : quoteInfo.totalDuration > MULTI_WEEK_DURATION_THRESHOLD
        ? labels[LabelKey.duration5orMoreWeeks]
        : labels[LabelKey.duration2to4Weeks];
  const isDurationUpgradeAvailable =
    !quoteInfo.isMultiQuote &&
    isDurationUpgradeEnabled &&
    !isGroupCourse &&
    isInternationalLanguageProgram &&
    quoteInfo.courseSegments[0]?.duration.value < MAX_DURATION_FOR_UPGRADE_WEEKS;
  const isUpsellAvailable =
    isDurationUpgradeAvailable ||
    isCourseUpgradeAvailable ||
    isAccommodationUpgradeAvailable ||
    isUpgradeOptionalItemsAvailable;

  const bannerImage = useMemo(
    () =>
      !isQuotesDestinationsLoading && currentSegment
        ? quotesContentCache.destinations.get(currentSegment.destinationCode)?.heroImage ??
          AppConfig.content.defaultBannerImage
        : "",
    [isQuotesDestinationsLoading, quote.id]
  );

  const schoolImage = useMemo(
    () =>
      !isQuotesDestinationsLoading && currentSegment
        ? quotesContentCache.destinations.get(currentSegment.destinationCode)?.schoolImage ??
          AppConfig.content.defaultSchoolImage
        : undefined,
    [isQuotesDestinationsLoading, currentSegment]
  );

  const destinationName = useMemo(
    () =>
      !isQuotesDestinationsLoading && quoteInfo.courseSegments.length
        ? getDestinationName(quoteInfo.courseSegments, quotesContentCache?.destinations)
        : "",
    [isQuotesDestinationsLoading, quoteInfo.courseSegments]
  );

  const factSheetLink = useMemo(
    () =>
      !isQuotesDestinationsLoading &&
      activeLanguage?.atlas &&
      currentSegment?.destinationCode &&
      quotesContentCache.destinations.get(currentSegment.destinationCode)?.name
        ? `${AppConfig.api.atlasWebsite}/public/${activeLanguage.atlas}/destinations/${mapFactSheetDestinationCode(currentSegment.destinationCode)}/school/fact-sheet/`
        : "",
    [isQuotesDestinationsLoading, activeLanguage, currentSegment]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (quoteData) {
      trackEvent(quote.opportunityUuid, StandardTrackingEvent.ViewItem, {
        currency: quoteData.prices.totalPrice.currency,
        value: quoteData.prices.totalPrice.amount,
        items: generatePriceQuoteEventItems(quoteData),
      });
    }
  }, [quote.id]);

  useEffect(() => {
    if (!quote || !quoteData.market || !quoteData.program || !market || !efcomProduct) {
      setCourseContent([]);
      return;
    }

    let cancelled = false;
    getCourseContentForQuote(market, courseContentProduct, quoteData.segments, additionalCourseCodes)
      .then((content) => {
        if (!cancelled) {
          setCourseContent(content);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCourseContent([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [quote.id, market, efcomProduct, courseContentProduct, quoteData.segments, isCourseUpgradeAvailable, upgradeCourseCode]);

  useEffect(() => {
    if (quoteDraft) {
      const newIndex = calculateCurrentSegmentIndex(quoteData, quoteInfo);
      setCurrentSegmentIndex(newIndex);
      setGroupCourse(quoteInfo.courseSegments[0].group ? true : false);
    }
  }, [quoteDraft.quoteDraft.quoteData.id]);

  return (
    <ContentLayout
      banner={<BannerImage url={bannerImage} size="base" />}
      stickySidePanel={true}
      topStaticCard={
        <div className="a-gap-sm flex flex-col">
          <QuoteHeader
            destinationName={destinationName}
            courseInfo={quoteInfo.courseInfo}
            startAt={quoteInfo.startAt}
            endAt={quoteInfo.endAt}
            duration={Enrich(weeksLabel, { numWeeks: quoteInfo.totalDuration })}
            totalPrice={quoteData.prices.totalPrice}
            payments={quote.payments}
            lgMinimized={false}
            isPreferred={isPreferred}
            onSetPreferred={
              isPreferredQuoteEnabled && canSetAsPreferred
                ? () => {
                    setPreferredQuoteId(quoteData.id);
                  }
                : undefined
            }
          />
          {!quoteDraft.isQuoteDraftEdited && (
            <div className="flex flex-col items-end">
              <Button
                size="base"
                intent="secondary-black"
                iconName="cloud-download-outlined"
                onPress={() => {
                  trackEvent(quote.opportunityUuid, CustomTrackingEvent.DownloadPdf);
                  window.open(quote.document.pdfUrl, "_blank");
                }}
              >
                {labels[LabelKey.pdf]}
              </Button>
            </div>
          )}
        </div>
      }
      sidePanel={
        <>
          <div className="a-gap-y-sm flex flex-col">
            <div className="lg:block">
              <BookNow
                isSaveInProgress={isSaveInProgress}
                setSaveInProgress={setIsSaveInProgress}
                quote={quote}
                quoteDraft={quoteDraft}
              />
            </div>
          </div>
          {factSheetLink && (
            <>
              <div
                className="active:scale-98 a-hover-scale shadow-card"
                onClick={() => {
                  trackEvent(quote.opportunityUuid, StandardTrackingEvent.SelectContent, {
                    content_type: ContentType.DestinationDetails,
                  });
                  window.open(factSheetLink, "_blank");
                }}
              >
                {schoolImage && currentSegment && (
                  <ImageCard
                    title={Enrich(labels[LabelKey.discoverDestination], {
                      destinationName:
                        quotesContentCache?.destinations.get(currentSegment.destinationCode)?.name ??
                        currentSegment.destinationCode,
                    })}
                    aspectRatio={IMAGE_CARD_ASPECT_RATIO}
                    image={<img alt="" src={schoolImage} />}
                    className="z-10"
                  >
                    <div className="flex flex-row items-center gap-2">
                      {labels[LabelKey.infoSheet]} <Icon size="sm" iconName="arrow-right" />
                    </div>
                  </ImageCard>
                )}
              </div>
              <div className="block lg:hidden">
                <Text variant="heading-5-bold" className="my-6">
                  {labels[LabelKey.bookNowLabel]}
                </Text>

                <BookNow
                  isSaveInProgress={isSaveInProgress}
                  setSaveInProgress={setIsSaveInProgress}
                  quote={quote}
                  quoteDraft={quoteDraft}
                />
              </div>
            </>
          )}
        </>
      }
      testId="quote-details"
    >
      <div className={`a-gap flex w-full flex-col lg:flex-row`} data-testid="quoteDetails-page">
        <div className="a-gap flex w-full flex-col">
          {[...(showRawQuote ? quoteDraft.quoteDraft.rawQuoteData : quoteData).segments]
            .filter((segment) => segment.type !== SegmentType.Break)
            .sort((a, b) => b.type - a.type)
            .map((segment, index, segments) => (
              <QuoteSegment
                key={index}
                segmentType={segment.type}
                lineItems={segment.lineItems
                  .filter((lineItem) => !lineItem.isOptional)
                  .map((lineItem) => {
                    const courseInfo = courseContent
                      ?.find((destination) => destination.destinationCode === segment.destinationCode)
                      ?.courses.find(
                        (course) =>
                          !!course.courseCode &&
                          !!segment.course?.code &&
                          course.courseCode.toUpperCase() === segment.course.code.toUpperCase()
                      );
                    return {
                      ...lineItem,
                      additionalDescription:
                        lineItem.courseType?.toUpperCase() === courseInfo?.courseCode?.toUpperCase() &&
                        courseInfo?.numberOfLessonsPerWeek
                          ? courseInfo.numberOfLessonsPerWeek
                          : lineItem.additionalDescription,
                    };
                  })}
                isMultiQuote={quoteInfo.isMultiQuote}
                destinationName={
                  quotesContentCache?.destinations.get(segment.destinationCode)?.name ?? segment.destinationCode
                }
                duration={Enrich(weeksLabel, { numWeeks: segment.duration?.value })}
                startAt={segment.startAt}
                endAt={segment.endAt}
                price={segment.price.totalPrice?.amount}
                currency={segment.price.totalPrice?.currency}
                payNowDiscountLabel={index === segments.length - 1 ? labels[LabelKey.payNowDiscount] : undefined}
                payNowDiscountAmount={index === segments.length - 1 ? payNowDiscountAmount : undefined}
              />
            ))}
          {quote.status !== QuotePaymentStatus.Paid && isUpsellAvailable && (
            <TBSection title={labels[LabelKey.upgrade]} isUpgrade={true}>
              {labels[LabelKey.optionalItemsAdditionalDescription] && (
                <Text variant="paragraph-body">{labels[LabelKey.optionalItemsAdditionalDescription]}</Text>
              )}
              {isUpgradeOptionalItemsAvailable && (
                <UpgradeOptionalItems
                  data-testid="upgrade-optional-items"
                  isSaveInProgress={isSaveInProgress}
                  setIsSaveInProgress={setIsSaveInProgress}
                  currentSegmentIndex={currentSegmentIndex}
                  isMultiQuote={quoteInfo.isMultiQuote}
                  quote={quote}
                  quoteDraft={quoteDraft.quoteDraft.quoteData}
                />
              )}
              <div>
                {isDurationUpgradeAvailable && (
                  <UpgradeDuration isSaveInProgress={isSaveInProgress} quote={quote} quoteDraft={quoteDraft} />
                )}
                {isCourseUpgradeAvailable && (
                  <div>
                    <Divider light className="my-4 h-px" />
                    <UpgradeCourse
                      content={courseContent
                        ?.find((destination) => destination.destinationCode === currentSegment.destinationCode)
                        ?.courses.find(
                          (course) =>
                            !!course.courseCode &&
                            !!upgradeCourseCode &&
                            course.courseCode.toUpperCase() === upgradeCourseCode.toUpperCase()
                        )}
                      isSaveInProgress={isSaveInProgress}
                      quote={quote}
                      quoteDraft={quoteDraft.quoteDraft}
                    />
                  </div>
                )}
                {isAccommodationUpgradeAvailable && (
                  <div>
                    <Divider light className="my-4 h-px" />
                    <UpgradeAccommodation
                      isSaveInProgress={isSaveInProgress}
                      quote={quote}
                      quoteDraft={quoteDraft.quoteDraft}
                    />
                  </div>
                )}
              </div>
            </TBSection>
          )}
        </div>
      </div>
    </ContentLayout>
  );
};

export default QuoteDetails;
